import { BaseApiService } from '../base/BaseApiService';

/** Uma linha telefônica que o cliente já possui na base TIM (CSV/importação em lote). */
export interface TimClientLine {
  numero: string | null;
  planoDados: string | null;
  /** Parcelas restantes por linha — null até import/webhook popular o dado. */
  parcelasRestantes?: number | null;
  /** Linha sem plano de dados (zerada). */
  zerado?: boolean;
}

/**
 * Dados do cliente na base TIM (mini API Python + proxy no GettelApi).
 * Espelha o contrato `GET /api/clients/tim-data/{cnpj}` — ver ideia
 * `ideas/consulta-dados-tim-cnpj-renegociacao.md`.
 *
 * `found: false` significa que o CNPJ não tem registro na base TIM; nesse caso os
 * demais campos não são enviados pela API e não devem ser lidos.
 */
export interface TimClientData {
  found: boolean;
  cnpj?: string | null;
  razaoSocial?: string | null;
  nomeRepresentante?: string | null;
  telefoneContato?: string | null;
  email?: string | null;
  /** CPF vindo da base TIM — nunca validado/cruzado com Receita. Ver `cpfConfiancaBaixa`. */
  cpf?: string | null;
  /** Quando true, o CPF deve ser exibido com aviso de baixa confiabilidade (pode estar incompleto). */
  cpfConfiancaBaixa?: boolean;
  rg?: string | null;
  valorFatura?: number | null;
  /** Moda de parcelas do consolidado/coleta (bruta, sem decay). */
  parcelas?: number | null;
  /**
   * Fidelidade efetiva: decay mensal de `parcelas` quando preenchida;
   * senão o valor legado do import.
   */
  fidelidadeMesesRestantes?: number | null;
  dataVencimento?: string | null;
  quantidadeLinhas?: number | null;
  linhasAptas?: number | null;
  acessosAtivos?: number | null;
  zerados?: number | null;
  inadimplente?: boolean | null;
  linhas?: TimClientLine[];
  /** Data do lote de importação (não é uma data por cliente) — usada no alerta de desatualização. */
  loteImportacaoData?: string | null;
}

export class TimDataService extends BaseApiService {
  private readonly basePath = '/clients/tim-data';

  /**
   * Consulta os dados TIM de um cliente pelo CNPJ.
   * Resposta é sempre 200 — usar `found` para saber se há dados (nunca lança para "não encontrado").
   */
  async fetchTimClientData(cnpj: string): Promise<TimClientData> {
    const cleanedCnpj = cnpj.replace(/\D/g, '');
    const response = await this.api.get<TimClientData>(`${this.basePath}/${cleanedCnpj}`);
    return response.data;
  }
}

export const timDataService = new TimDataService();

/** Função utilitária standalone, conforme contrato pedido pelo worker de backend. */
export const fetchTimClientData = (cnpj: string): Promise<TimClientData> =>
  timDataService.fetchTimClientData(cnpj);
